import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  async request(endpoint, { method = 'GET', body, headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      // A 403 means the SERVER — the only authority on access — refused. The
      // client's capability list may simply be out of date (an administrator
      // can revoke a capability at any moment), so announce it: AuthContext
      // listens and re-reads /auth/me/permissions, which is how the client
      // converges on the server rather than arguing with it. The caller still
      // gets a thrown Error with an honest message to show.
      if (res.status === 403 && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('acuteos:forbidden', { detail: { endpoint } }));
      }

      const message =
        payload?.error?.message ||
        (res.status === 403
          ? 'You do not have permission to do that. Your access may have changed — reload the page to refresh it.'
          : `Request failed (${res.status})`);
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Auth ----
  /**
   * Registers a new (pending) account. Payload: { email, name, password,
   * confirmPassword, facility, role, licenceId, termsAccepted }. Never
   * returns a session — see AuthContext.register.
   */
  register(payload) {
    return this.request('/auth/register', { method: 'POST', body: payload });
  }

  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }

  me() {
    return this.request('/auth/me');
  }

  /**
   * The signed-in user's effective capabilities — `{ role, capabilities }`,
   * where `capabilities` is a list of `page-slug:capability` strings read
   * straight from the same `role_permissions` rows the server's own gate
   * (`requirePermission`) reads. This is what the client gates its NAVIGATION
   * on; it is never an enforcement decision. See AuthContext.
   */
  myPermissions() {
    return this.request('/auth/me/permissions');
  }

  /** Always returns an enumeration-safe `{ message }` (plus a dev-only `devResetToken`). */
  forgotPassword(email) {
    return this.request('/auth/forgot-password', { method: 'POST', body: { email } });
  }

  /** Pre-render gate for the Reset Password form — `{ valid: boolean }`. */
  validateResetToken(token) {
    return this.request(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`);
  }

  resetPassword({ token, password, confirmPassword }) {
    return this.request('/auth/reset-password', { method: 'POST', body: { token, password, confirmPassword } });
  }

  /** Requires an active session. Returns { message, user, token } — a fresh token so the caller's own session survives. */
  changePassword({ currentPassword, password, confirmPassword, revokeOtherSessions }) {
    return this.request('/auth/change-password', {
      method: 'PATCH',
      body: { currentPassword, password, confirmPassword, revokeOtherSessions },
    });
  }

  /**
   * The caller's own session as the server observes it — `{ userId, email,
   * name, role, lastSignInAt, sessionGeneration, issuedAt, expiresAt,
   * ipAddress, userAgent }`. Not a device list: the backend keeps no
   * per-session rows (see auth.service.js `session`).
   */
  getSession() {
    return this.request('/auth/session');
  }

  /** Invalidates every other JWT for this account. Returns { message, user, token } — a fresh token for this session. */
  revokeOtherSessions() {
    return this.request('/auth/sessions/revoke-others', { method: 'POST' });
  }

  // ---- Users (Admin only) ----
  listUsers({ limit = 50, offset = 0 } = {}) {
    return this.request(`/users?limit=${limit}&offset=${offset}`);
  }

  /** `{ items, total }` — search/filter/paginate the Admin roster. */
  getUserRoster({ search, roleId, status, facility, limit = 20, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (roleId) params.set('roleId', roleId);
    if (status) params.set('status', status);
    if (facility) params.set('facility', facility);
    params.set('limit', limit);
    params.set('offset', offset);
    return this.request(`/users/roster?${params.toString()}`);
  }

  getUser(id) {
    return this.request(`/users/${id}`);
  }

  /** Returns the created user plus a dev-only `devResetToken` (no email service in this prototype). */
  createUser({ name, email, facility, licenceId, roleIds, status }) {
    return this.request('/users', { method: 'POST', body: { name, email, facility, licenceId, roleIds, status } });
  }

  updateUser(id, { name, email, facility, licenceId, status, roleIds }) {
    return this.request(`/users/${id}`, {
      method: 'PATCH',
      body: { name, email, facility, licenceId, status, roleIds },
    });
  }

  manageUserRoles(id, roleIds) {
    return this.request(`/users/${id}/roles`, { method: 'PATCH', body: { roleIds } });
  }

  deactivateUser(id) {
    return this.request(`/users/${id}/deactivate`, { method: 'POST' });
  }

  reactivateUser(id) {
    return this.request(`/users/${id}/reactivate`, { method: 'POST' });
  }

  /** `{ message, devResetToken? }` */
  forceUserPasswordReset(id) {
    return this.request(`/users/${id}/force-password-reset`, { method: 'POST' });
  }

  // ---- Facilities (Network Ops Admin, Transfer Coordinator, Bed Manager, Network Duty Manager, Auditor) ----
  /**
   * Each row also carries capacity signals derived from that facility's own
   * ward_capacity declarations — `occupancyPct`, `lastAdtSyncAt`,
   * `adtAgeMinutes`, `adtStaleness` — all null when the facility has never
   * declared a ward.
   */
  listFacilities({ search, facilityType, tier, capability } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (facilityType) params.set('facilityType', facilityType);
    if (tier) params.set('tier', tier);
    if (capability) params.set('capability', capability);
    const qs = params.toString();
    return this.request(`/facilities${qs ? `?${qs}` : ''}`);
  }

  /** Network Ops Admin only. Registers a facility and records it to the evaluation ledger. */
  createFacility(payload) {
    return this.request('/facilities', { method: 'POST', body: payload });
  }

  getFacility(id) {
    return this.request(`/facilities/${id}`);
  }

  /** Network Ops Admin only. Records an audit_log entry for whichever fields actually changed. */
  updateFacility(id, patch) {
    return this.request(`/facilities/${id}`, { method: 'PATCH', body: patch });
  }

  getFacilityAuditLog(id) {
    return this.request(`/facilities/${id}/audit-log`);
  }

  /** Minimal { id, name, facility_type } facility picker — open to any authenticated role (not gated to Facility Registry viewers). */
  getFacilityDirectory() {
    return this.request('/facilities/directory');
  }

  // ---- Specialty Coverage / On-Call Rosters (Network Ops Admin, Transfer Coordinator, Network Duty Manager) ----
  /**
   * `at` filters to schedules live at a single instant; `from`/`to` (sent
   * together) filters to schedules overlapping a shift window — what the
   * roster's Current Shift / Night Shift / Next 24 Hours selector asks for.
   */
  listCoverage(facilityId, { specialty, at, from, to } = {}) {
    const params = new URLSearchParams();
    if (specialty) params.set('specialty', specialty);
    if (at) params.set('at', at);
    if (from && to) {
      params.set('from', from);
      params.set('to', to);
    }
    const qs = params.toString();
    return this.request(`/facilities/${facilityId}/coverage${qs ? `?${qs}` : ''}`);
  }

  /** `{ gaps: string[] }` — mandatory specialties with no active coverage at `at` (default now). */
  getCoverageGaps(facilityId, at) {
    const qs = at ? `?at=${encodeURIComponent(at)}` : '';
    return this.request(`/facilities/${facilityId}/coverage/gaps${qs}`);
  }

  /** Network Ops Admin only. */
  createCoverage(facilityId, payload) {
    return this.request(`/facilities/${facilityId}/coverage`, { method: 'POST', body: payload });
  }

  /** Network Ops Admin only. Also used for the quick "Override Status" action (send just `{ status }`). */
  updateCoverage(facilityId, id, patch) {
    return this.request(`/facilities/${facilityId}/coverage/${id}`, { method: 'PATCH', body: patch });
  }

  // ---- Critical Equipment Availability (Facility Registry viewer/editor roles) ----
  /** One row per routing-critical equipment item this facility has installed, each with its current Operational/Down status. */
  listEquipmentStatus(facilityId) {
    return this.request(`/facilities/${facilityId}/equipment-status`);
  }

  /** Network Ops Admin only. `estimatedRestorationAt` is required when `status` is 'Down'. */
  updateEquipmentStatus(facilityId, { equipment, status, estimatedRestorationAt }) {
    return this.request(`/facilities/${facilityId}/equipment-status`, {
      method: 'PATCH',
      body: { equipment, status, estimatedRestorationAt },
    });
  }

  /** The deterministic routing engine's query surface: facilities that currently have `equipment` installed and Operational. */
  getFacilitiesAvailableForEquipment(equipment) {
    return this.request(`/facilities/equipment-status/available?equipment=${encodeURIComponent(equipment)}`);
  }

  // ---- Ward Bed Capacity (Bed Manager, Transfer Coordinator, Network Duty Manager) ----
  /** One row per ward unit ever declared for this facility, with computed available_beds + staleness. */
  listWardCapacity(facilityId) {
    return this.request(`/facilities/${facilityId}/ward-capacity`);
  }

  /** `{ totalOperationalBeds, currentNetworkCensus, availableAcuteBeds, occupancyRate, status }` */
  getWardCapacityOverview(facilityId) {
    return this.request(`/facilities/${facilityId}/ward-capacity/overview`);
  }

  /** Bed Manager only. Upserts the (facility, wardUnit) declaration and resets its staleness clock. */
  declareWardCapacity(facilityId, payload) {
    return this.request(`/facilities/${facilityId}/ward-capacity`, { method: 'PATCH', body: payload });
  }

  /** On-demand HL7v2/FHIR ADT feed connectivity check — `{ connected, checkedAt, message }`. */
  syncWardCapacityFeed(facilityId) {
    return this.request(`/facilities/${facilityId}/ward-capacity/sync-feed`, { method: 'POST' });
  }

  /** Network-wide facility x bed-class heatmap — `{ grid, wardClasses, summary }`. */
  getCapacityHeatmap({ bedClass, region } = {}) {
    const params = new URLSearchParams();
    if (bedClass) params.set('bedClass', bedClass);
    if (region) params.set('region', region);
    const qs = params.toString();
    return this.request(`/facilities/capacity-heatmap${qs ? `?${qs}` : ''}`);
  }

  // ---- Transfer Intake (Referring Clinician, Transfer Coordinator) ----
  /** Submits a structured acute transfer request — arms the SLA clock and logs the event to the evaluation ledger. */
  submitTransferRequest(payload) {
    return this.request('/transfer-requests', { method: 'POST', body: payload });
  }

  /** Feeds the SBAR Handoff Workspace's case-context bar. */
  getTransferRequest(id) {
    return this.request(`/transfer-requests/${id}`);
  }

  // ---- SBAR Handoff Workspace (Referring Clinician edits/signs; Accepting Clinician, Transfer Coordinator, Auditor read) ----
  /** Gets (generating on first visit) the SBAR document, populated with the deterministic AI draft. */
  getSbarDocument(transferId) {
    return this.request(`/transfer-requests/${transferId}/sbar`);
  }

  /** "Regenerate SBAR Draft" — refreshes all four sections from structured transfer data (blocked once signed). */
  regenerateSbarDraft(transferId) {
    return this.request(`/transfer-requests/${transferId}/sbar/regenerate`, { method: 'POST' });
  }

  /** "Save Progress" — persists clinician edits pre-signature. */
  saveSbarProgress(transferId, { situation, background, assessment, recommendation }) {
    return this.request(`/transfer-requests/${transferId}/sbar`, {
      method: 'PATCH',
      body: { situation, background, assessment, recommendation },
    });
  }

  /** "Digitally Sign & Lock SBAR Handoff" — computes the SHA-256 integrity hash and locks the document. */
  signSbarDocument(transferId) {
    return this.request(`/transfer-requests/${transferId}/sbar/sign`, { method: 'POST', body: { attestationConfirmed: true } });
  }

  /** "Download Portable SBAR Summary" — returns the raw text/plain body + filename (only once digitally signed). */
  async exportSbarDocument(transferId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/transfer-requests/${transferId}/sbar/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => null);
      const error = new Error(payload?.error?.message || `Request failed (${res.status})`);
      error.status = res.status;
      throw error;
    }
    const disposition = res.headers.get('content-disposition') || '';
    const filenameMatch = disposition.match(/filename="([^"]+)"/);
    return { blob: await res.blob(), filename: filenameMatch?.[1] || 'sbar-handoff.txt' };
  }

  // ---- Coordinator Workstation: candidate matching (Transfer Coordinator, Network Duty Manager, Auditor) ----
  /** The minimal "Pending Match" queue — transfer requests with no accepting facility yet, most urgent first. */
  listPendingMatchTransfers() {
    return this.request('/transfer-requests/pending-match');
  }

  /** The deterministic (zero-LLM) candidate ranking + rule trace for a transfer request. */
  getCandidateMatches(transferId) {
    return this.request(`/transfer-requests/${transferId}/candidate-matches`);
  }

  /** Commits the accepting facility. `overrideJustification` is required unless `facilityId` is the top-ranked eligible candidate. */
  selectAcceptingFacility(transferId, { facilityId, overrideClassification, overrideJustification }) {
    return this.request(`/transfer-requests/${transferId}/accepting-facility`, {
      method: 'PATCH',
      body: { facilityId, overrideClassification, overrideJustification },
    });
  }

  /** Matched-or-later transfers (Matched, OFFER_SENT, ESCALATED), each with a computed live slaState (Green/Amber/Red). */
  listActiveTransfers() {
    return this.request('/transfer-requests/active');
  }

  /** Dispatches the transfer offer to the already-matched accepting facility and arms the live decision SLA clock. */
  dispatchTransferOffer(transferId) {
    return this.request(`/transfer-requests/${transferId}/dispatch-offer`, { method: 'POST' });
  }

  /** Assigns transport provider/call sign and logs departure — transitions the transfer to IN_TRANSIT. */
  coordinateTransport(transferId, { transportMode, providerName, ambulanceCallSign, dispatchTime, departureAt }) {
    return this.request(`/transfer-requests/${transferId}/coordinate-transport`, {
      method: 'POST',
      body: { transportMode, providerName, ambulanceCallSign, dispatchTime, departureAt },
    });
  }

  /** Confirms patient arrival at the destination facility and closes the transfer (terminal ARRIVED status). */
  confirmArrival(transferId, { arrivalAt, receivingBedNote }) {
    return this.request(`/transfer-requests/${transferId}/confirm-arrival`, {
      method: 'POST',
      body: { arrivalAt, receivingBedNote },
    });
  }

  // ---- Clinician Decision Portal (Accepting Clinician, Referring Clinician, Transfer Coordinator) ----
  /** The incoming-offer inbox — OFFER_SENT transfers, each with a computed live slaState. */
  listOffers() {
    return this.request('/transfer-requests/offers');
  }

  /** Tap 1 "Accept Transfer" — marks ACCEPTED and reserves the receiving bed. */
  acceptTransfer(transferId) {
    return this.request(`/transfer-requests/${transferId}/accept`, { method: 'POST' });
  }

  /** Tap 1 "Decline Transfer" + Tap 2 "Submit Governed Decline" — `reasonCode` is mandatory (no free-text-only declines). */
  declineTransfer(transferId, { reasonCode, note }) {
    return this.request(`/transfer-requests/${transferId}/decline`, { method: 'POST', body: { reasonCode, note } });
  }

  // ---- Network Command Wallboard (Network Duty Manager, Transfer Coordinator, Network Ops Admin, Auditor) ----
  /** Top-ribbon KPIs + the three wallboard lanes (escalations, pendingDecisions, inFlight), each with live SLA clock state and corridor facility names. */
  getWallboard() {
    return this.request('/transfer-requests/wallboard');
  }

  /** Network Duty Manager only. Reroutes an ESCALATED transfer to a new facility, re-dispatches the offer with a fresh SLA clock, and logs a supervisor-hashed critical event. */
  dutyManagerOverride(transferId, { facilityId, overrideClassification, justificationNote, signOffConfirmed }) {
    return this.request(`/transfer-requests/${transferId}/duty-manager-override`, {
      method: 'POST',
      body: { facilityId, overrideClassification, justificationNote, signOffConfirmed },
    });
  }

  // ---- Ledger Explorer (Auditor, Network Duty Manager, admin — read-only) ----
  /** `{ items, total, limit, offset }` — each item carries its true chain sequence #, actor, summary, and hash / prevHash. */
  getLedgerEvents({ transferId, facilityId, eventType, dateFrom, dateTo, q, limit, offset } = {}) {
    const params = new URLSearchParams();
    if (transferId) params.set('transferId', transferId);
    if (facilityId) params.set('facilityId', facilityId);
    if (eventType) params.set('eventType', eventType);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    if (q) params.set('q', q);
    if (limit) params.set('limit', limit);
    if (offset !== undefined) params.set('offset', offset);
    const qs = params.toString();
    return this.request(`/audit-log${qs ? `?${qs}` : ''}`);
  }

  getLedgerEvent(id) {
    return this.request(`/audit-log/${id}`);
  }

  /** Recomputes the SHA-256 hash chain from scratch and validates it against what's stored — `{ valid, totalBlocks, brokenAt, verifiedAt }`. */
  verifyLedgerChain() {
    return this.request('/audit-log/verify-chain');
  }

  /** Exports the currently-filtered ledger stream — returns the raw blob + filename (mirrors exportSbarDocument's pattern). */
  async exportLedger({ transferId, facilityId, eventType, dateFrom, dateTo, q, format }) {
    const params = new URLSearchParams({ format });
    if (transferId) params.set('transferId', transferId);
    if (facilityId) params.set('facilityId', facilityId);
    if (eventType) params.set('eventType', eventType);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    if (q) params.set('q', q);

    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/audit-log/export?${params.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => null);
      const error = new Error(payload?.error?.message || `Request failed (${res.status})`);
      error.status = res.status;
      throw error;
    }
    const disposition = res.headers.get('content-disposition') || '';
    const filenameMatch = disposition.match(/filename="([^"]+)"/);
    return { blob: await res.blob(), filename: filenameMatch?.[1] || `ledger-export.${format}` };
  }

  // ---- Transfer Performance & Analytics (Auditor, Network Duty Manager, Network Ops Admin, admin — read-only) ----
  /** BEACON performance & decline analytics — median request-to-decision time vs. the prior equal-length window, stage timings by acuity, decline Pareto by facility/shift, SLA compliance by acuity, retained volume. */
  getTransferPerformanceReport({ dateFrom, dateTo, facilityId, corridor } = {}) {
    const params = new URLSearchParams();
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    if (facilityId) params.set('facilityId', facilityId);
    if (corridor) params.set('corridor', corridor);
    const qs = params.toString();
    return this.request(`/reports/transfer-performance${qs ? `?${qs}` : ''}`);
  }

  /**
   * "Generate DOH Audit Bundle" — a real ZIP archive (event chain, rule
   * versions, clinical sign-offs, and a manifest of genuine SHA-256
   * checksums), scoped to a single transferId OR a date range/facility/
   * corridor batch. Mirrors exportLedger/exportSbarDocument's raw-blob
   * download pattern (not JSON-enveloped).
   */
  async downloadDohAuditBundle({ transferId, dateFrom, dateTo, facilityId, corridor } = {}) {
    const params = new URLSearchParams();
    if (transferId) params.set('transferId', transferId);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    if (facilityId) params.set('facilityId', facilityId);
    if (corridor) params.set('corridor', corridor);

    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/reports/doh-audit-bundle?${params.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => null);
      const error = new Error(payload?.error?.message || `Request failed (${res.status})`);
      error.status = res.status;
      throw error;
    }
    const disposition = res.headers.get('content-disposition') || '';
    const filenameMatch = disposition.match(/filename="([^"]+)"/);
    return { blob: await res.blob(), filename: filenameMatch?.[1] || 'doh-audit-bundle.zip' };
  }

  // ---- Multi-EMR Integration Settings (Network Ops Admin / admin edit; Auditor view-only) ----
  /** Vendors, protocols, message types, credential types, AcuteOS bed classes — for building the connection form. */
  getEmrConnectionsMeta() {
    return this.request('/emr-connections/meta');
  }

  /** One row per connection: facilities served, health status, and mapping completeness. */
  listEmrConnections() {
    return this.request('/emr-connections');
  }

  getEmrConnection(id) {
    return this.request(`/emr-connections/${id}`);
  }

  /** Network Ops Admin / admin only. `credential: { credentialType, secret }` is write-only — never echoed back. */
  createEmrConnection(payload) {
    return this.request('/emr-connections', { method: 'POST', body: payload });
  }

  updateEmrConnection(id, patch) {
    return this.request(`/emr-connections/${id}`, { method: 'PATCH', body: patch });
  }

  /** Blocked with a 400 while any bed-class mapping row is unmapped, or a 409 if the facility already has an active connection. */
  setEmrConnectionEnabled(id, enabled) {
    return this.request(`/emr-connections/${id}/enabled`, { method: 'PATCH', body: { enabled } });
  }

  deleteEmrConnection(id) {
    return this.request(`/emr-connections/${id}`, { method: 'DELETE' });
  }

  /** "Test Connection" — a read-only probe only (FHIR capability GET / MLLP TCP connect), never writes. */
  testEmrConnection(id) {
    return this.request(`/emr-connections/${id}/test`, { method: 'POST' });
  }

  /** Recent config/test events for this connection, from the evaluation ledger. */
  getEmrConnectionHealthLog(id) {
    return this.request(`/emr-connections/${id}/health-log`);
  }

  getEmrConnectionMappings(id) {
    return this.request(`/emr-connections/${id}/mappings`);
  }

  /** Replaces the full source-code -> AcuteOS bed-class mapping set in one save. `bedClass: null` marks a row unmapped. */
  saveEmrConnectionMappings(id, mappings) {
    return this.request(`/emr-connections/${id}/mappings`, { method: 'PUT', body: { mappings } });
  }

  // ---- Roles (Admin only) ----
  listRoles() {
    return this.request('/roles');
  }

  createRole({ name, description, category }) {
    return this.request('/roles', { method: 'POST', body: { name, description, category } });
  }

  updateRole(id, { name, description, category }) {
    return this.request(`/roles/${id}`, { method: 'PATCH', body: { name, description, category } });
  }

  deleteRole(id) {
    return this.request(`/roles/${id}`, { method: 'DELETE' });
  }

  duplicateRole(id) {
    return this.request(`/roles/${id}/duplicate`, { method: 'POST' });
  }

  /**
   * { role, matrix: [{ pageSlug, pageName, module,
   *   canView, canCreate, canEdit, canDelete, canExport,
   *   canExecute, canSign, canOverride }] }
   */
  getRolePermissions(roleId) {
    return this.request(`/roles/${roleId}/permissions`);
  }

  saveRolePermissions(roleId, permissions) {
    return this.request(`/roles/${roleId}/permissions`, { method: 'PUT', body: { permissions } });
  }
}

export default new ApiService();
